<template>
  <div class="applepay">
    <div class="applepay__form">
      <DocumentInput
        v-model="form.document"
        isPaymentCardStep
        @validate="handleDocumentInput"
      />
      <div class="applepay__installments position-relative">
        <label class="am-input-label">
          {{ $t('steps.payment.card.installment.label') }}
        </label>
        <select
          v-model="form.installments"
          class="select-installments"
          :required="true"
        >
          <option
            v-for="installment in installments"
            :key="installment.id"
            :value="installment"
          >
            {{ installment.label }}
          </option>
        </select>
        <AmIconMaterial
          type="round"
          name="expand_more"
          size="12"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
import currencyFilterMixin from '@/components/mixins/currencyFilterMixin'
import sendToFlashlight from '@/utils/flashlightService'
import ApplePayConfigs from '@/utils/ApplePay/configs'
import DocumentInput from '../common/DocumentInput.vue'
import mountInstallment from '../utils/mountInstallment'

export default {
  name: 'PaymentApplePay',
  components: {
    DocumentInput,
  },
  mixins: [currencyFilterMixin],
  data() {
    return {
      form: {
        document: '',
        installments: null,
      },
      errors: {
        document: 'error',
        installments: null,
      },
    }
  },
  computed: {
    ...mapState('Checkout', {
      cart: (state) => state.cart,
    }),
    ...mapGetters('Checkout', ['getCart']),
    ...mapGetters('Customer', ['getDocumentNumber']),
    ...mapGetters('Cart', [
      'getInstallments',
      'getCurrencyCode',
      'getIsInternational',
      'getTotal',
      'getHasApplePay',
    ]),
    installments() {
      return this.getInstallments.map((installments) =>
        mountInstallment({
          properties: installments,
          isInternational: this.getIsInternational,
          currencyCode: this.getCurrencyCode,
          total: this.getTotal,
          currencyFilter: this.$options.filters.currency,
        })
      )
    },
  },
  watch: {
    form: {
      deep: true,
      handler() {
        // Atualiza os erros
        this.errors.document =
          !this.form.document || this.form.document.trim() === '' ? 'error' : 'success'
        this.errors.installments = !this.form.installments ? 'error' : 'success'

        // Atualiza o estado de desabilitado
        this.updateDisabledState()
      },
    },
    getInstallments: {
      immediate: true,
      handler() {
        this.setInstallment()
        this.form.installments = this.installments[this.installments.length - 1]
      },
    },
  },
  mounted() {
    this.SET_CURRENT_STEP(4)
    this.SET_PAYMENT_TYPE('applepay')
  },
  async created() {
    const isApplePayNotSupported = (await this.isApplePaySupported()) === false
    const isApplePayNotEnabled = !this.getHasApplePay
    if (isApplePayNotEnabled || isApplePayNotSupported) {
      this.$router.push({
        name: 'new.stepPayment',
        params: { history: true },
        query: { pl: this.getPl?.pl },
      })
      return
    }

    this.form.document = this.getDocumentNumber
    this.form.installments = this.installments[this.installments.length - 1]
  },
  methods: {
    ...mapMutations('Cart', {
      setSelectedInstallment: 'SET_SELECTED_INSTALLMENT',
    }),
    ...mapMutations('Checkout', [
      'SET_CURRENT_STEP',
      'SET_STATUS_CART_LOADING',
      'SET_CUSTOMER_DOCUMENT_NUMBER',
      'SET_DISABLED_STATE_PAYMENT',
      'SET_PAYMENT_TYPE',
    ]),
    ...mapActions('ApplePay', ['processApplePayPayment', 'isApplePaySupported']),
    setInstallment() {
      this.form.installments = mountInstallment({
        properties: this.searchPaymentByInstallment(this.getInstallmentDefault),
        isInternational: this.getIsInternational,
        currencyCode: this.getCurrencyCode,
        total: this.getTotal,
        currencyFilter: this.$options.filters.currency,
      })
      this.setInstallmentChangeToStore()
    },
    searchPaymentByInstallment(idPayment) {
      return this.getInstallments.find(
        (installmentProperties) => installmentProperties.installment === idPayment
      )
    },
    setInstallmentChangeToStore() {
      this.setSelectedInstallment(this.form.installments)
    },
    previousStep() {
      this.$router.go(-1)
    },
    async nextStep() {
      this.SET_CUSTOMER_DOCUMENT_NUMBER(this.form.document)
      this.SET_DISABLED_STATE_PAYMENT(true)

      try {
        const canGoToNextStep = await this.processApplePayPayment({
          cart: this.getCart,
          installment: this.form.installments.id.toString(),
        })
        if (!canGoToNextStep) {
          this.SET_DISABLED_STATE_PAYMENT(false)
          this.SET_STATUS_CART_LOADING(false)
        } else {
          const payload = {
            cart: this.getCart.hash,
            payment_type: ApplePayConfigs.key,
          }

          sendToFlashlight(payload)

          this.$router.push({
            name: 'new.stepPaymentApplePaySuccess',
            params: { history: true, creditCardDetails: {} },
            query: { pl: this.getPl?.pl },
          })
        }
      } catch (error) {
        this.$router.push({
          name: 'new.stepPaymentError',
          params: { history: true },
          query: { pl: this.getPl?.pl },
        })
      } finally {
        this.SET_DISABLED_STATE_PAYMENT(false)
        this.SET_STATUS_CART_LOADING(false)
      }
    },
    handleDocumentInput(validation) {
      this.errors.document = validation ? 'success' : 'error'
      this.updateDisabledState()
    },
    updateDisabledState() {
      const hasErrors = Object.values(this.errors).some((error) => error === 'error')
      this.SET_DISABLED_STATE_PAYMENT(hasErrors)
    },
  },
}
</script>

<style lang="scss" scoped>
.applepay {
  width: 100%;

  &__form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  &__installments {
    .select-installments {
      padding-left: 8px;
      padding-right: 28px;
      height: 42px;
      color: #333333;
      border: 1px solid #d2d6de;
      width: 100%;
      font-size: 14px;
      line-height: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      border-radius: 5px;
      grid-template-areas: 'select';
      align-items: center;
      display: grid;
      position: relative;
      -moz-appearance: none;
      -webkit-appearance: none;
      appearance: none;
      background-color: #fff;
    }

    span {
      position: absolute;
      bottom: 8px;
      right: 5px;
      pointer-events: none;
    }
  }
}
</style>
